import img from "../assets/images/shutterstock_699442570-min.jpg";
import { useState, useRef } from "react";
import constants from "../constants/constants";
import SEO, { pageSEO } from "../components/seo.component";

export default function Reports() {
  const [popup, setPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    clientType: "",
    interests: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      interests: checked
        ? [...prev.interests, value]
        : prev.interests.filter((interest) => interest !== value),
    }));
    // Clear error when user selects an interest
    if (formErrors.interests) {
      setFormErrors(prev => ({
        ...prev,
        interests: ""
      }));
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation function
  const validateForm = () => {
    const errors = {};

    // Check required fields
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.clientType.trim()) {
      errors.clientType = "Please select your client type";
    }

    if (formData.interests.length === 0) {
      errors.interests = "Please select at least one interest";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  async function submitForm(event) {
    event.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Create HTML message body with all form data
    const htmlMessage = `
      <h2 style="color: #2563eb; margin-bottom: 20px;">New Lead Interested in Real Estate Reports</h2>
      
      <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h3 style="color: #1e293b; margin-bottom: 15px;">Contact Information</h3>
        <p><strong>Name:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Client Type:</strong> ${formData.clientType}</p>
      </div>

      <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h3 style="color: #1e293b; margin-bottom: 15px;">Interested Reports</h3>
        <ul style="list-style-type: disc; padding-left: 20px;">
          ${formData.interests.map(interest => `<li style="margin-bottom: 8px;">${interest}</li>`).join('')}
        </ul>
      </div>

      <div style="background-color: #dcfce7; padding: 15px; border-radius: 8px; border-left: 4px solid #16a34a;">
        <p style="margin: 0; color: #166534; font-weight: 600;">
          This request was submitted from the website contact form.
        </p>
      </div>
    `;

    const emailFormData = new FormData();
    emailFormData.append('data', JSON.stringify({
      recipientEmails: [constants.AGENT_EMAIL],
      senderEmail: process.env.REACT_APP_AGENT_EMAIL,
      subject: "New Lead Interested in Real Estate Reports",
      content: htmlMessage,
      senderName: formData.name,
      replyTo: formData.email,
    }));

    let EmailApiUrl = process.env.REACT_APP_NODE_ENV === "production" ? "https://3.138.143.209/global/send-email" : "http://3.138.143.209/global/send-email";

    try {
      const response = await fetch(EmailApiUrl, {
        method: "POST",
        body: emailFormData,
      });

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      const res = await response.json();
      console.log("res", res);

      if (res?.message === "success") {
        console.log("Success", res);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          name: "",
          email: "",
          clientType: "",
          interests: [],
        });
        setFormErrors({});
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const seoConfig = pageSEO.reports();
  return (
    <>
      <SEO {...seoConfig} />
      <div className="max-w-[1100px] pt-[100px] mx-auto grid md:grid-cols-2 px-6 gap-x-24 gap-y-10 min-h-[70vh] mb-12">
        <div>
          <h1 className="text-4xl text-black font-mont ">
            FREE REAL ESTATE REPORTS
          </h1>

          <p className="font-semibold text-med-grey my-5">
            Get exclusive buyer & seller real estate reports to help you with
            your next purchase or sale. Get insider tips and tricks, and learn
            how to invest your time and money wisely in the real estate market.
            Just complete the form below, select all the reports you'd like to
            receive and have them emailed to your inbox right away. Get started
            now!
          </p>
          <form ref={formRef} onSubmit={submitForm}>
            <input
              type="hidden"
              name="access_key"
              value={constants.EMAIL_ACCESS_KEY}
            />
            <input
              type="hidden"
              name="subject"
              value="New Lead Interested in Real Estate Reports"
            />

            <div className="form-item">
              <label className="form-label">Name*</label>
              <input
                className={`form-input ${formErrors.name ? 'border-red-500' : ''}`}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Full Name"
              />
              {formErrors.name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Email*</label>
              <input
                className={`form-input ${formErrors.email ? 'border-red-500' : ''}`}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email Address"
              />
              {formErrors.email && (
                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">I am a*</label>
              <select
                className={`form-input ${formErrors.clientType ? 'border-red-500' : ''}`}
                name="clientType"
                value={formData.clientType}
                onChange={handleInputChange}
              >
                <option value="">Please Select</option>
                <option value="Buyer">Buyer</option>
                <option value="Seller">Seller</option>
                <option value="Renter">Renter</option>
                <option value="Other">Other</option>
              </select>
              {formErrors.clientType && (
                <p className="text-red-500 text-sm mt-1">{formErrors.clientType}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">I am interested in:*</label>
              <div className={`grid grid-cols-2 gap-3 mt-3 ${formErrors.interests ? 'border border-red-500 rounded p-2' : ''}`}>
                <div className="cursor-pointer">
                  <input
                    className="mr-2"
                    type="checkbox"
                    id="buying-guides"
                    value="Buying guides"
                    checked={formData.interests.includes("Buying guides")}
                    onChange={handleCheckboxChange}
                  />
                  <label htmlFor="buying-guides">Guides for Buying</label>
                </div>
                <div>
                  <input
                    className="mr-2"
                    type="checkbox"
                    id="selling-guides"
                    value="Selling guides"
                    checked={formData.interests.includes("Selling guides")}
                    onChange={handleCheckboxChange}
                  />
                  <label htmlFor="selling-guides">Guides for Selling</label>
                </div>
                <div>
                  <input
                    className="mr-2"
                    type="checkbox"
                    id="choosing-realtor"
                    value="Choosing realtor"
                    checked={formData.interests.includes("Choosing realtor")}
                    onChange={handleCheckboxChange}
                  />
                  <label htmlFor="choosing-realtor">Choosing a Realtor</label>
                </div>
                <div>
                  <input
                    className="mr-2"
                    type="checkbox"
                    id="renovations"
                    value="Renovations"
                    checked={formData.interests.includes("Renovations")}
                    onChange={handleCheckboxChange}
                  />
                  <label htmlFor="renovations">Home Renovations</label>
                </div>
              </div>
              {formErrors.interests && (
                <p className="text-red-500 text-sm mt-1">{formErrors.interests}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-black text-white px-10 py-3 mt-3 rounded hover:bg-logo-main transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
        <div className="overflow-hidden h-[600px] ">
          <img className="h-full w-full object-cover" src={img} />
        </div>
      </div>
      {popup && (
        <>
          <div className="backdrop" />
          <div className="popup">
            <h5 className="font-bold text-black text-lg">{popup.subject}</h5>
            <p className="font-semibold text-med-grey tracking-wide mb-3">
              {popup.message}
            </p>
            <button onClick={() => setPopup(false)} className="btn-rounded">
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
